import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Drawer, tokens } from 'components/v3';
import FormShellHeader from '../../components/FormShellHeader';
import { fieldTypeLabel } from '../../shared/fieldTypes';
import { useBuilder } from '../BuilderContext';
import useFieldFocus from '../hooks/useFieldFocus';
import FieldProperties from '../properties/FieldProperties';
import FormSettings from '../properties/FormSettings';
import Canvas from './Canvas';
import { fieldName } from './FieldCard';
import Palette from './Palette';

// Desktop builder (≥768, plan §5.7): Palette | Canvas | Properties at
// ≥1280 ("wide"); below that ("narrow") Palette | Canvas, with Properties
// and Form Settings in a right Drawer opened by selecting a field or the
// header's gear. The page owns which drawer is open (`drawer`,
// `onDrawer`) so it can close it before showing a confirm dialog.

// Room the sticky columns leave at the bottom for the unsaved-changes bar,
// so nothing docked (Properties, the pinned "Add a saved field") sits under
// it. The bar's real height is measured while it shows (it grows when its
// pills or hint wrap); SAVE_BAR_SPACE is the fallback before that.
export const SAVE_BAR_SPACE = 96;
const EDGE = 16;

export const stickyColumn = (stickyTop, saveBarSpace) => ({
  position: 'sticky',
  top: stickyTop,
  maxHeight: `calc(100vh - ${stickyTop + (saveBarSpace || EDGE)}px)`,
  overflowY: 'auto',
});

const findSaveBar = () =>
  document.querySelector('[role="region"][aria-label="Unsaved changes"]');

// → the save bar's height while `dirty` (0 when clean).
export const useSaveBarSpace = (dirty) => {
  const [space, setSpace] = useState(SAVE_BAR_SPACE);
  useEffect(() => {
    const bar = dirty ? findSaveBar() : null;
    if (!bar || typeof window.ResizeObserver !== 'function') return undefined;
    const measure = () => {
      const { height } = bar.getBoundingClientRect();
      if (height) setSpace(Math.ceil(height));
    };
    measure();
    const observer = new window.ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [dirty]);
  return dirty ? space : 0;
};

const DesktopBuilder = ({
  header,
  banner,
  renderHeader,
  drawer,
  onDrawer,
  onAddType,
  onAddSaved,
  onOpenAddSheet,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const { state, dispatch, layout, isLocked } = useBuilder();
  const wide = layout.mode === 'wide';
  const selected =
    state.working.fields.find((f) => f.id === state.selectedId) || null;
  const sticky = stickyColumn(
    layout.stickyTop,
    useSaveBarSpace(!!header.dirty)
  );
  useFieldFocus(state.working.fields);

  // The field drawer belongs to a selection: once that field is gone
  // (deleted from the drawer, say) the drawer is done, so a later tap on
  // the palette doesn't reopen it unasked.
  const fieldDrawerOrphaned = drawer === 'field' && !selected;
  useEffect(() => {
    if (fieldDrawerOrphaned) onDrawer(null);
  }, [fieldDrawerOrphaned, onDrawer]);

  // A second click on the selected card lets it go (the button is a toggle).
  // A locked card (w9-plan §6.5) selects nothing.
  const selectField = (id) => {
    if (isLocked && isLocked(id)) return;
    const next = state.selectedId === id ? null : id;
    dispatch({ type: 'SELECT', id: next });
    if (!wide && next) onDrawer('field');
  };

  const headerProps = {
    ...header,
    showSettingsButton: !wide,
    onOpenSettings: () => onDrawer('settings'),
  };

  return (
    <>
      {renderHeader ? (
        renderHeader(headerProps)
      ) : (
        <FormShellHeader {...headerProps} />
      )}
      {banner}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: wide
            ? '264px minmax(0, 1fr) 340px'
            : '248px minmax(0, 1fr)',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Palette onAddType={onAddType} onAddSaved={onAddSaved} style={sticky} />
        <div
          role="presentation"
          style={{ minWidth: 0 }}
          onClick={() => dispatch({ type: 'SELECT', id: null })}
        >
          <Canvas onSelectField={selectField} onAddField={onOpenAddSheet} />
        </div>
        {wide ? (
          <aside
            aria-label={selected ? 'Field settings' : 'Form settings'}
            style={{
              ...sticky,
              borderRadius: 16,
              background: t.card,
              border: '1px solid ' + t.border,
            }}
          >
            {selected ? <FieldProperties field={selected} /> : <FormSettings />}
          </aside>
        ) : null}
      </div>
      {wide ? null : (
        <>
          <Drawer
            open={drawer === 'field' && !!selected}
            onClose={() => onDrawer(null)}
            title={selected ? fieldName(selected) : ''}
            subtitle={selected ? `${fieldTypeLabel(selected)} field` : null}
            width={380}
          >
            {selected ? (
              <FieldProperties field={selected} showHeader={false} />
            ) : null}
          </Drawer>
          <Drawer
            open={drawer === 'settings'}
            onClose={() => onDrawer(null)}
            title="Form Settings"
            subtitle="Applies to the whole form"
            width={380}
          >
            <FormSettings showHeader={false} />
          </Drawer>
        </>
      )}
    </>
  );
};

DesktopBuilder.propTypes = {
  header: PropTypes.object.isRequired,
  banner: PropTypes.node,
  // (headerProps) → node, in place of FormShellHeader (w9-plan §6.5).
  renderHeader: PropTypes.func,
  drawer: PropTypes.oneOf(['field', 'settings']),
  onDrawer: PropTypes.func.isRequired,
  onAddType: PropTypes.func.isRequired,
  onAddSaved: PropTypes.func.isRequired,
  onOpenAddSheet: PropTypes.func.isRequired,
};

DesktopBuilder.defaultProps = {
  banner: null,
  renderHeader: undefined,
  drawer: null,
};

export default DesktopBuilder;
